import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, PlusCircle, Wallet, Bell, LogOut, Menu, X,
  Users, BarChart3, ListChecks, Settings2, Download, MessageSquare, FileText
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const userNav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/segnala", label: "Segnala immobile", icon: PlusCircle },
  { to: "/compensi", label: "Merito fidelizzazione", icon: Wallet },
  { to: "/messaggi", label: "Messaggi", icon: MessageSquare },
  { to: "/contratto", label: "Contratto", icon: FileText },
  { to: "/impostazioni", label: "Impostazioni", icon: Settings2 },
];
const adminNav = [
  { to: "/admin", label: "Analytics", icon: BarChart3 },
  { to: "/admin/segnalazioni", label: "Segnalazioni", icon: ListChecks },
  { to: "/admin/messaggi", label: "Messaggi", icon: MessageSquare },
  { to: "/admin/programmi", label: "Programmi merito", icon: Settings2 },
  { to: "/admin/contratti", label: "Registro firme", icon: FileText },
  { to: "/admin/report", label: "Report", icon: Download },
];

export function DashboardLayout({ children, title, admin = false }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [msgUnread, setMsgUnread] = useState(0);
  const [showNotifs, setShowNotifs] = useState(false);

  const nav = admin ? adminNav : userNav;

  useEffect(() => {
    if (!admin) api.get("/notifications").then((r) => setNotifs(r.data)).catch(() => {});
    if (!admin) api.get("/messages/unread-count").then((r) => setMsgUnread(r.data.unread)).catch(() => {});
  }, [admin, location.pathname]);

  const unread = notifs.filter((n) => !n.read).length;
  const markRead = async () => {
    setShowNotifs(!showNotifs);
    if (!showNotifs && unread > 0) {
      await api.post("/notifications/read-all");
      setNotifs(notifs.map((n) => ({ ...n, read: true })));
    }
  };

  const Sidebar = (
    <aside className="w-64 shrink-0 bg-[#0A1128] text-white flex flex-col h-full">
      <div className="px-6 py-6 border-b border-white/10"><Logo /></div>
      <nav className="flex-1 px-3 py-6 space-y-1">
        {nav.map((n) => {
          const active = location.pathname === n.to;
          return (
            <Link key={n.to} to={n.to} onClick={() => setOpen(false)} data-testid={`side-${n.to.replace(/\//g, "-")}`}
              className={`flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium transition-colors ${
                active ? "bg-[#D4AF37] text-[#0A1128]" : "text-white/70 hover:bg-white/10 hover:text-white"
              }`}>
              <n.icon className="h-5 w-5" /> <span className="flex-1">{n.label}</span>
              {n.to === "/messaggi" && msgUnread > 0 && (
                <span className="h-5 min-w-5 px-1.5 rounded-full bg-[#D4AF37] text-[11px] font-bold text-[#0A1128] flex items-center justify-center" data-testid="msg-unread-badge">{msgUnread}</span>
              )}
            </Link>
          );
        })}
        {!admin && (user?.role === "admin" || user?.role === "manager") && (
          <Link to="/admin" className="flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium text-[#D4AF37] hover:bg-white/10">
            <Users className="h-5 w-5" /> Area Admin
          </Link>
        )}
      </nav>
      <div className="px-3 py-4 border-t border-white/10">
        <div className="px-4 pb-3">
          <p className="text-sm font-medium text-white truncate">{user?.name}</p>
          <p className="text-xs text-white/50 truncate">{user?.email}</p>
        </div>
        <button onClick={() => { logout(); navigate("/"); }} data-testid="logout-btn"
          className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium text-white/70 hover:bg-white/10 hover:text-white">
          <LogOut className="h-5 w-5" /> Esci
        </button>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <div className="hidden lg:block sticky top-0 h-screen">{Sidebar}</div>
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-0 h-full">{Sidebar}</div>
        </div>
      )}
      <div className="flex-1 min-w-0">
        <header className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b border-slate-200">
          <div className="flex items-center justify-between px-5 sm:px-8 h-16">
            <div className="flex items-center gap-3">
              <button className="lg:hidden text-slate-700" onClick={() => setOpen(true)} data-testid="mobile-menu-btn"><Menu /></button>
              <h1 className="font-display text-xl font-semibold text-slate-900">{title}</h1>
            </div>
            {!admin && (
              <div className="relative">
                <button onClick={markRead} data-testid="notifications-bell" className="relative p-2 rounded-lg hover:bg-slate-100">
                  <Bell className="h-5 w-5 text-slate-700" />
                  {unread > 0 && <span className="absolute top-1 right-1 h-4 w-4 rounded-full bg-[#D4AF37] text-[10px] font-bold text-[#0A1128] flex items-center justify-center">{unread}</span>}
                </button>
                {showNotifs && (
                  <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-auto rounded-xl border border-slate-200 bg-white shadow-xl p-2" data-testid="notifications-dropdown">
                    {notifs.length === 0 ? (
                      <p className="p-4 text-sm text-slate-500">Nessuna notifica.</p>
                    ) : notifs.map((n) => (
                      <div key={n.id} className="px-3 py-3 rounded-lg hover:bg-slate-50">
                        <p className="text-sm text-slate-800">{n.message}</p>
                        <p className="text-xs text-slate-400 mt-1">{new Date(n.created_at).toLocaleString("it-IT")}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </header>
        <main className="p-5 sm:p-8">{children}</main>
      </div>
    </div>
  );
}
